import { create } from 'zustand';

/**
 * BookmarkUI state interface
 * Contains UI-only state for bookmarks, without duplicating server state
 */
interface BookmarkUIState {
  // UI loading state for individual operations
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  
  // Error message for UI display
  errorMessage: string | null;
  setErrorMessage: (errorMessage: string | null) => void;
  
  // Filter/Search state
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  
  // Bookmark creation modal state
  isAddingBookmark: boolean;
  setIsAddingBookmark: (isAddingBookmark: boolean) => void;
  
  // Bookmark note editor state
  editingNoteId: string | null;
  setEditingNoteId: (bookmarkId: string | null) => void;
  
  // Bookmark expanded state for mobile view
  expandedBookmarkId: string | null;
  setExpandedBookmarkId: (bookmarkId: string | null) => void;
  
  // Reset all UI state to defaults
  resetUIState: () => void;
}

/**
 * Bookmark UI Store
 * 
 * Manages UI-specific state for bookmarks without duplicating server data.
 * This is part of the React Query migration strategy to separate UI state 
 * from server state.
 */
export const useBookmarkUIStore = create<BookmarkUIState>((set) => ({
  // UI loading states
  isSubmitting: false,
  setIsSubmitting: (isSubmitting) => set({ isSubmitting }),
  
  // Error handling
  errorMessage: null,
  setErrorMessage: (errorMessage) => set({ errorMessage }),
  
  // Filter/Search
  searchTerm: '',
  setSearchTerm: (searchTerm) => set({ searchTerm }),
  
  // Bookmark creation
  isAddingBookmark: false,
  setIsAddingBookmark: (isAddingBookmark) => set({ isAddingBookmark }),
  
  // Note editor state
  editingNoteId: null,
  setEditingNoteId: (editingNoteId) => set({ editingNoteId }),
  
  // Mobile view expanded state
  expandedBookmarkId: null, 
  setExpandedBookmarkId: (expandedBookmarkId) => set({ expandedBookmarkId }),
  
  // Reset all UI state
  resetUIState: () => set({
    isSubmitting: false,
    errorMessage: null,
    searchTerm: '',
    isAddingBookmark: false,
    editingNoteId: null,
    expandedBookmarkId: null
  })
}));